import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, GraduationCap, Users } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/use-profile";

type RoleSelection = "student" | "teacher" | "parent" | null;

export default function Onboarding() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, profile, loading: profileLoading } = useProfile();
  const [role, setRole] = useState<RoleSelection>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!profileLoading) {
      if (!user) {
        setLocation("/auth");
      } else if (profile?.onboarding_completed) {
        setLocation(profile.role === "teacher" ? "/teacher-dashboard" : "/");
      }
    }
  }, [user, profile, profileLoading, setLocation]);

  const handleSaveRole = async () => {
    if (!role || !user) return;

    setIsSaving(true);
    try {
      const { error } = await supabase.from('users').upsert({
        id: user.id,
        email: user.email,
        role: role,
        onboarding_completed: true,
        updated_at: new Date().toISOString()
      }, { onConflict: 'id' });

      if (error) {
        console.error("Supabase upsert error:", error);
        throw new Error(error.message || 'Failed to save profile');
      }

      toast({
        title: "Setup Complete",
        description: `Welcome to Addvance as a ${role}!`,
      });

      queryClient.invalidateQueries({ queryKey: ["profile"] });

      // Redirect based on role and force hard reload to refresh profile state
      if (role === 'teacher') {
        window.location.href = "/teacher-dashboard";
      } else {
        window.location.href = "/";
      }
    } catch (err: any) {
      toast({
        title: "Error saving profile",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (profileLoading || (user && profile?.onboarding_completed)) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg transition-all duration-300 ease-in-out hover:shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-extrabold tracking-tight">
            How will you use Addvance?
          </CardTitle>
          <CardDescription className="text-base mt-2">
            Select your role to personalize your experience
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Button
            variant={role === "student" ? "default" : "outline"}
            className="h-32 flex flex-col items-center justify-center gap-4 transition-all"
            onClick={() => setRole("student")}
          >
            <GraduationCap className="h-10 w-10" />
            <span className="text-lg font-medium">I am a Student</span>
          </Button>

          <Button
            variant={role === "teacher" ? "default" : "outline"}
            className="h-32 flex flex-col items-center justify-center gap-4 transition-all"
            onClick={() => setRole("teacher")}
          >
            <Users className="h-10 w-10" />
            <span className="text-lg font-medium">I am a Teacher</span>
          </Button>
          
          <Button
            variant={role === "parent" ? "default" : "outline"}
            className="h-32 flex flex-col items-center justify-center gap-4 sm:col-span-2 transition-all"
            onClick={() => setRole("parent")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span className="text-lg font-medium">I am a Parent</span>
          </Button>
        </CardContent>
        <CardFooter className="flex justify-between items-center bg-muted/20 p-6 border-t mt-4 rounded-b-xl">
          <div className="text-sm text-muted-foreground mr-4">
            You can change this later in settings.
          </div>
          <Button 
            onClick={handleSaveRole} 
            disabled={!role || isSaving}
            className="min-w-[120px] transition-all"
            size="lg"
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}